/**
 * Auth store — React context backed by localStorage for the access token.
 *
 * MVP scope: we persist only the short-lived access token. A 401 from any API
 * call clears the session and bounces to /login (wired via configureClient).
 * TODO(scaffold): persist + rotate the refresh token (POST /v1/auth/refresh)
 * so sessions survive access-token expiry without a re-login.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { EntitlementDto } from '@appbuilder/shared';
import {
  authApi,
  configureClient,
  type LoginRequest,
  type MeResponse,
  type SignupRequest,
} from '../api/client';

const TOKEN_KEY = 'appbuilder.accessToken';

export interface AuthUser {
  id: string;
  email: string;
  role: string;
  displayName?: string;
}

interface AuthState {
  /** undefined = still resolving session on boot; null = signed out. */
  user: AuthUser | null | undefined;
  entitlement: EntitlementDto | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (req: LoginRequest) => Promise<void>;
  signup: (req: SignupRequest) => Promise<void>;
  logout: () => void;
  /** Re-fetch /auth/me (e.g. after returning from Stripe Checkout). */
  refreshMe: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function writeToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage unavailable (private mode) — token stays in memory only */
  }
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const tokenRef = useRef<string | null>(readToken());
  const [token, setTokenState] = useState<string | null>(tokenRef.current);
  const [user, setUser] = useState<AuthUser | null | undefined>(undefined);
  const [entitlement, setEntitlement] = useState<EntitlementDto | null>(null);

  const setToken = useCallback((next: string | null) => {
    tokenRef.current = next;
    writeToken(next);
    setTokenState(next);
  }, []);

  const clearSession = useCallback(() => {
    setToken(null);
    setUser(null);
    setEntitlement(null);
  }, [setToken]);

  // Wire the API client to read the live token and react to 401s once on mount.
  useEffect(() => {
    configureClient(
      () => tokenRef.current,
      () => clearSession(),
    );
  }, [clearSession]);

  const loadMe = useCallback(async () => {
    const me: MeResponse = await authApi.me();
    setUser(me.user);
    setEntitlement(me.entitlement);
  }, []);

  // Resolve the persisted session on boot.
  useEffect(() => {
    if (!tokenRef.current) {
      setUser(null);
      return;
    }
    loadMe().catch(() => clearSession());
  }, [loadMe, clearSession]);

  const login = useCallback(
    async (req: LoginRequest) => {
      const res = await authApi.login(req);
      setToken(res.tokens.accessToken);
      await loadMe();
    },
    [setToken, loadMe],
  );

  const signup = useCallback(
    async (req: SignupRequest) => {
      const res = await authApi.signup(req);
      setToken(res.tokens.accessToken);
      await loadMe();
    },
    [setToken, loadMe],
  );

  const logout = useCallback(() => {
    // Best-effort server revoke; clear locally regardless.
    authApi.logout().catch(() => {});
    clearSession();
  }, [clearSession]);

  const refreshMe = useCallback(async () => {
    if (tokenRef.current) await loadMe();
  }, [loadMe]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      entitlement,
      token,
      isAuthenticated: Boolean(token && user),
      login,
      signup,
      logout,
      refreshMe,
    }),
    [user, entitlement, token, login, signup, logout, refreshMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
